/* eslint-disable @typescript-eslint/no-explicit-any */
import * as React from 'react';
import { oc } from 'ts-optchain';
import _ from 'lodash';

import { Alert, Link } from 'omakase';
import { IconDelivery, IconPlate, IconShipping, IconTicket, IconToGo } from '@tock/icons';
import {
  IconDelivery as IconDeliveryLegacy,
  IconDeliveryScooter,
  IconEvents,
  IconPrivateDining,
  IconShipping as IconShippingLegacy,
} from 'ui/icons';
import { IconMigrationUtil } from 'omakase/utils/IconMigrationUtil';
import { SafetyMeasuresCard } from 'components/business/safety-measures/SafetyMeasuresCard';
import { ScrollListNavigation, ScrollListNavigationItem, ScrollListNavigationPanel } from 'ui';
import { consumer } from '@tock/coretypes';
import {
  getRemainingEventTicketText,
  getRemainingTicketsForEvent,
  isGAEventType,
  isOfferedOnOneDateAndAtOneTime,
} from 'utils/offerings';
import Offering from 'components/business/offering/Offering';
import OfferingsStatusMessage from 'components/business/offering/OfferingsStatusMessage';

interface Props {
  business: consumer.IBusinessConfig;
  delivery?: consumer.Offering[];
  container?: React.ReactInstance | (() => React.ReactInstance | null) | null;
  events?: consumer.Offering[];
  hasMultipleOfferingTypes: boolean;
  hasOfferings?: boolean;
  nextRelease: any;
  onAddToWaitlist(experienceId?: number): void;
  pickup?: consumer.Offering[];
  reservations?: consumer.Offering[];
  shipping?: consumer.Offering[];
  showWaitlist: boolean;
  soldOut: boolean;
  ticketGroups: consumer.ICalendarTicketGroup[];
  isWidget?: boolean;
}

type JumpNavValue = 'reservations' | 'pickup' | 'events' | 'delivery' | 'shipping';

const SeparatedExperiencesJump: React.FunctionComponent<Props> = ({
  business,
  delivery,
  events,
  hasMultipleOfferingTypes,
  hasOfferings,
  nextRelease,
  onAddToWaitlist,
  pickup,
  reservations,
  shipping,
  showWaitlist,
  soldOut,
  ticketGroups,
  container,
  isWidget,
}) => {
  const hasReservations = !_.isEmpty(reservations);
  const hasPickup = !_.isEmpty(pickup);
  const hasDelivery = !_.isEmpty(delivery);
  const hasShipping = !_.isEmpty(shipping);
  const hasEvents = !_.isEmpty(events);

  const jumpNavOptions: JumpNavValue[] = _.compact([
    hasReservations ? 'reservations' : undefined,
    hasEvents ? 'events' : undefined,
    hasPickup ? 'pickup' : undefined,
    hasDelivery ? 'delivery' : undefined,
    hasShipping ? 'shipping' : undefined,
  ]);

  const firstJumpNavOption = _.first(jumpNavOptions);

  const [jumpNavValuesInView, setJumpNavValuesInView] = React.useState<
    Record<JumpNavValue, boolean>
  >({} as Record<JumpNavValue, boolean>);
  const [activeJumpNav, setActiveJumpNav] = React.useState<JumpNavValue | undefined>(
    firstJumpNavOption
  );

  React.useEffect(() => {
    const jumpNavOpptionsInView = jumpNavOptions.filter((option) => jumpNavValuesInView[option]);
    if (jumpNavOpptionsInView.length > 0) {
      setActiveJumpNav(jumpNavOpptionsInView[0]);
    }
  }, [jumpNavOptions, jumpNavValuesInView]);

  const rootMargin = '-117px 0px 0px 0px';
  const threshold = 0.05;

  const renderOffering = (offering: consumer.Offering, idx: number) => {
    const showRemainingTickets =
      isOfferedOnOneDateAndAtOneTime(offering) && isGAEventType(offering.type); // Never show remaining ticket count for seated event: https://tock.atlassian.net/browse/TK-1253
    const remainingTickets = showRemainingTickets
      ? getRemainingTicketsForEvent(offering.id, ticketGroups)
      : undefined;
    const callToActionHint = remainingTickets
      ? getRemainingEventTicketText(remainingTickets)
      : undefined;
    return (
      <Offering
        showWaitlist={showWaitlist}
        callToActionHint={callToActionHint}
        key={offering.id}
        offering={offering}
        onAddToWaitlist={onAddToWaitlist}
        business={business}
        listIndex={idx}
      />
    );
  };

  const handleSelectJumpNavItem = (value: JumpNavValue) => {
    window.analytics.sendEvent('Offering', 'Offering category selected on business page', value);
  };

  const isAuthorized = oc(business).isAuthorized(false);

  //header plus height of nav
  const mobileHeaderOffset = isWidget ? 134 : 117;
  const smHeaderOffset = isWidget ? 154 : 141;

  return (
    <>
      {business.infoBoxText && business.infoBoxText.trim() && (
        <Alert
          severity="info"
          className="Consumer-offeringsInfoBox"
          action={
            business.infoBoxLink ? (
              <Link target="_blank" href={business.infoBoxLink}>
                Learn more
              </Link>
            ) : undefined
          }
          data-testid="annoucement-info-box"
        >
          {business.infoBoxText}
        </Alert>
      )}
      {!soldOut && hasOfferings && isAuthorized && (
        <div className="Consumer-offeringsStatusMessage Consumer-offeringStatusMessage-jump">
          <OfferingsStatusMessage
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            businessName={business.name!}
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            businessTicketsAvailableUntil={business.ticketsAvailableUntil!}
            hasOfferings={hasOfferings}
            nextRelease={nextRelease}
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            offeringsDisabled={business.bookingsDisabled!}
            soldOut={soldOut}
          />
        </div>
      )}
      {business.domainName ? <SafetyMeasuresCard businessName={business.name || ''} /> : null}
      {hasMultipleOfferingTypes && (
        <ScrollListNavigation
          className="Consumer-reservation-nav"
          offset={mobileHeaderOffset}
          offsetSm={smHeaderOffset}
          onChange={handleSelectJumpNavItem}
          value={activeJumpNav}
          container={container}
        >
          {hasReservations && (
            <ScrollListNavigationItem
              icon={
                <IconMigrationUtil legacyIcon={<IconPrivateDining />} newIcon={<IconPlate />} />
              }
              aria-label="Skip to Reservations"
              value="reservations"
              label="Reservations"
            />
          )}
          {hasEvents && (
            <ScrollListNavigationItem
              icon={<IconMigrationUtil legacyIcon={<IconEvents />} newIcon={<IconTicket />} />}
              aria-label="Skip to Events"
              value="events"
              label="Events"
            />
          )}
          {hasPickup && (
            <ScrollListNavigationItem
              icon={
                <IconMigrationUtil legacyIcon={<IconDeliveryLegacy />} newIcon={<IconToGo />} />
              }
              aria-label="Skip to pickup"
              value="pickup"
              label="Pickup"
            />
          )}
          {hasDelivery && (
            <ScrollListNavigationItem
              icon={
                <IconMigrationUtil
                  legacyIcon={<IconDeliveryScooter />}
                  newIcon={<IconDelivery />}
                />
              }
              aria-label="Skip to Delivery"
              value="delivery"
              label="Delivery"
            />
          )}
          {hasShipping && (
            <ScrollListNavigationItem
              icon={
                <IconMigrationUtil legacyIcon={<IconShippingLegacy />} newIcon={<IconShipping />} />
              }
              aria-label="Skip to shipping"
              value="shipping"
              label="Shipping"
            />
          )}
        </ScrollListNavigation>
      )}
      <ul data-testid="business-experience-list">
        {hasReservations && [
          <li key="ReservationsList">
            <ScrollListNavigationPanel
              value="reservations"
              component="ul"
              className="Consumer-reservationsList"
              inView={{
                threshold,
                rootMargin,
                onChange: (isInView) =>
                  setJumpNavValuesInView({
                    ...jumpNavValuesInView,
                    reservations: isInView,
                  }),
              }}
            >
              {_.map(reservations, (offering, idx) => renderOffering(offering, idx))}
            </ScrollListNavigationPanel>
          </li>,
        ]}
        {hasEvents && [
          <li key="EventsList">
            <ScrollListNavigationPanel
              value="events"
              component="ul"
              className="Consumer-eventsList"
              inView={{
                threshold,
                rootMargin,
                onChange: (isInView) =>
                  setJumpNavValuesInView({
                    ...jumpNavValuesInView,
                    events: isInView,
                  }),
              }}
            >
              {_.map(events, (offering, idx) =>
                renderOffering(
                  offering,
                  (reservations?.length || 0) +
                    (pickup?.length || 0) +
                    (delivery?.length || 0) +
                    (shipping?.length || 0) +
                    idx
                )
              )}
            </ScrollListNavigationPanel>
          </li>,
        ]}
        {hasPickup && (
          <li key="PickupList">
            <ScrollListNavigationPanel
              value="pickup"
              component="ul"
              className="Consumer-reservationsList"
              inView={{
                threshold,
                rootMargin,
                onChange: (isInView) =>
                  setJumpNavValuesInView({
                    ...jumpNavValuesInView,
                    pickup: isInView,
                  }),
              }}
            >
              {_.map(pickup, (offering, idx) =>
                renderOffering(offering, (reservations?.length || 0) + idx)
              )}
            </ScrollListNavigationPanel>
          </li>
        )}
        {hasDelivery && (
          <li key="DeliveryList">
            <ScrollListNavigationPanel
              value="delivery"
              component="ul"
              className="Consumer-reservationsList"
              inView={{
                threshold,
                rootMargin,
                onChange: (isInView) =>
                  setJumpNavValuesInView({
                    ...jumpNavValuesInView,
                    delivery: isInView,
                  }),
              }}
            >
              {_.map(delivery, (offering, idx) =>
                renderOffering(offering, (reservations?.length || 0) + (pickup?.length || 0) + idx)
              )}
            </ScrollListNavigationPanel>
          </li>
        )}
        {hasShipping && (
          <li key="ShippingList">
            <ScrollListNavigationPanel
              value="shipping"
              component="ul"
              className="Consumer-reservationsList"
              inView={{
                threshold,
                rootMargin,
                onChange: (isInView) =>
                  setJumpNavValuesInView({
                    ...jumpNavValuesInView,
                    shipping: isInView,
                  }),
              }}
            >
              {_.map(shipping, (offering, idx) =>
                renderOffering(
                  offering,
                  (reservations?.length || 0) +
                    (pickup?.length || 0) +
                    (delivery?.length || 0) +
                    idx
                )
              )}
            </ScrollListNavigationPanel>
          </li>
        )}
      </ul>
    </>
  );
};

export default SeparatedExperiencesJump;
